import {
  activateEhsConfigurationQuery,
  CreateEhsListItemInput,
  createEhsListItemMutation,
  ehsConfigurationTypesQuery,
  EhsListsFilterInput,
  ehsListsQuery,
  fetchEHSConfigurationsQuery,
  UpdateEhsListItemInput,
  updateEhsListItemMutation,
} from "../components/supportFunctions/graphql/EhsConfigurationsQueries";
import { PaginationQueryObject } from "../components/supportFunctions/graphql/Queries";
import { ActivateEhsConfigurationInput } from "../hooks/useEhsConfigurationMutations";
import BaseService from "./BaseService";
import UsersStore from "./UsersStore";
import { ehsConfigDetailsUpdateMutation } from "../components/supportFunctions/graphql/EhsConfigQueries";

class EHSConfigService extends BaseService {
  constructor() {
    super(process.env.EHS_BRIDGE_BASE_URL);
  }

  // Route for REST API (Uploading and Downloading Configuration Files)
  // All other routes are GraphQL
  static ehsUrl = `/api/v1/EhsConfiguration`;

  async fetchEHSConfigurations(
    filterInput: any,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const response = await this.api.post(
        "/graphql",
        fetchEHSConfigurationsQuery(filterInput, paginationInput)
      );
      const configs = response.data.data.ehsConfigurations;
      const configsWithUsers = await UsersStore.setUsersDetails(
        configs.items,
        ["createdBy"],
        (item: any) => item.createdBy.id
      );
      return {
        items: configsWithUsers,
        pageInfo: configs.pageInfo,
      };
    } catch (error) {
      throw error;
    }
  }

  async downloadConfigFile(customerId: string, configurationFileId: string) {
    try {
      const response = await this.api.get(EHSConfigService.ehsUrl, {
        params: { customerId, configurationFileId },
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async uploadConfigFile(
    file: File,
    customerId: string,
    configTypeId: string,
    description: string,
    isActive: boolean
  ) {
    const formData = new FormData();
    formData.append("ConfigurationFile", file);
    formData.append("CustomerId", customerId);
    formData.append("ConfigurationTypeId", configTypeId);
    formData.append("Description", description ? description : "");
    formData.append("IsActive", isActive.toString());

    try {
      const response = await this.api.post(EHSConfigService.ehsUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateConfigFile(input) {
    try {
      const response = await this.api.post(
        "/graphql",
        ehsConfigDetailsUpdateMutation(input)
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async activateEhsConfiguration(input: ActivateEhsConfigurationInput) {
    try {
      const response = await this.api.post(
        "/graphql",
        activateEhsConfigurationQuery(input)
      );
      return response.data.data.activateEhsConfiguration;
    } catch (error) {
      throw error;
    }
  }

  async getEhsConfigurationTypes(input: { customerId: string }) {
    try {
      const response = await this.api.post(
        "/graphql",
        ehsConfigurationTypesQuery(input)
      );

      return response.data.data.ehsConfigurationTypes;
    } catch (error) {
      throw error;
    }
  }

  async getEhsLists(
    filterInput: EhsListsFilterInput,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const response = await this.api.post(
        "/graphql",
        ehsListsQuery(filterInput, paginationInput)
      );

      return response.data.data.ehsLists;
    } catch (error) {
      throw error;
    }
  }

  async createEhsListItem(input: CreateEhsListItemInput) {
    try {
      const response = await this.api.post(
        "/graphql",
        createEhsListItemMutation(input)
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateEhsListItem(input: UpdateEhsListItemInput) {
    try {
      const response = await this.api.post(
        "/graphql",
        updateEhsListItemMutation(input)
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default new EHSConfigService();
